import { useState, useRef, useEffect } from "react";
import { formatINR } from "@/utils/goldSilverParser";

export default function PriceCalculator({currentPriceData=null, isGold=true, colors={}}){
    const [gstOpen, setGstOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const gstRef = useRef(null);

    useEffect(() => {
        if (!errorMessage) return;
        const timeout = setTimeout(() => setErrorMessage(""), 3000);
        return () => clearTimeout(timeout);
    }, [errorMessage]);

    useEffect(() => {
        const handleOutside = (e) => {
            if (gstRef.current && !gstRef.current.contains(e.target)) {
                setGstOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const initRate = isGold ? currentPriceData?.oneGram?.carat24 : currentPriceData?.oneGram;
    const initMakingCharge = Math.round(0.1 * initRate);
    const initGstCharge = Math.round(0.03 * initRate);
    const [calcForm, setCalcForm] = useState({
        calcType:"weight",
        weight:1,
        rate: initRate,
        baseValue: initRate,
        amount: initRate + initMakingCharge + initGstCharge,
        purity: isGold? "24K" : "",
        making: 10,
        makingCharge: initMakingCharge,
        gst: 3,
        gstCharge: initGstCharge,
    })

    const validate = (name, value) => {
        if(name=="weight"){
            if(value && !RegExp(/^\d*\.?\d*$/).test(value)) return "Weight can only contain numbers.";
            const decimals = value?.toString().split(".")[1];
            if(decimals && decimals.length > 2) return "Weight can have at most 2 decimal places.";
            if(value<0) return "Weight cannot be negative.";
            const digits = value?.toString().replace("-", "").split(".")[0];
            if(digits && digits.length > 15) return "Weight cannot exceed 15 digits.";
            return null;
        }
        if(name=="amount"){
            if(value && !RegExp(/^-?\d+(\.\d+)?$/).test(value)) return "Amount can only contain numbers.";
            if(value<0) return "Amount cannot be negative.";
            const digits = value?.toString().replace("-", "").split(".")[0];
            if(digits && digits.length > 15) return "Amount cannot exceed 15 digits.";
            return null;
        }
        if(name=="making"){
            if(value && !RegExp(/^-?\d+(\.\d+)?$/).test(value)) return "Making charge can only contain numbers.";
            if(value<0) return "Making charge cannot be negative.";
            if(value>100) return "Making charge cannot exceed 100%.";
            return null;
        }
        if(name=="gst"){
            if(value && !RegExp(/^-?\d+(\.\d+)?$/).test(value)) return "GST can only contain numbers.";
            if(value<0) return "GST cannot be negative.";
            return null;
        }
        return null;
    }

    const computeFromWeight = (weight, rate, making, gst) => {
        const baseValue = Math.round(weight * rate);
        const makingCharge = Math.round((making / 100) * baseValue);
        const gstCharge = Math.round((gst / 100) * baseValue);
        return { baseValue, makingCharge, gstCharge, amount: baseValue + makingCharge + gstCharge };
    };

    const computeFromAmount = (amount, rate, making, gst) => {
        const factor = 1 + making / 100 + gst / 100;
        const baseValue = factor > 0 ? amount / factor : 0;
        const weight = rate > 0 ? Number((baseValue / rate).toFixed(2)) : 0;
        const makingCharge = Math.round((making / 100) * baseValue);
        const gstCharge = Math.round((gst / 100) * baseValue);
        return { baseValue, weight, makingCharge, gstCharge };
    };

    const handleChange = (name, value) => {
        const validationError = validate(name, value);
        if(validationError){
            setErrorMessage(validationError);
            return;
        };

        if(name=="purity"){
            const rateMap = { "24K": currentPriceData?.oneGram?.carat24, "22K": currentPriceData?.oneGram?.carat22, "18K": currentPriceData?.oneGram?.carat18, "14K": currentPriceData?.oneGram?.carat14 };
            const newRate = isGold ? rateMap[value] : currentPriceData?.oneGram;
            setCalcForm((prev) => {
                const computed = computeFromWeight(parseFloat(prev.weight) || 0, newRate, prev.making, prev.gst);
                return { ...prev, purity: value, rate: newRate, ...computed };
            });
            return;
        }

        if(name=="weight"){
            const newWeight = parseFloat(value) || 0;
            setCalcForm((prev) => {
                const computed = computeFromWeight(newWeight, prev.rate, prev.making, prev.gst);
                return { ...prev, weight: value, ...computed };
            });
            return;
        }

        if(name=="amount"){
            const newAmount = parseFloat(value) || 0;
            setCalcForm((prev) => {
                const computed = computeFromAmount(newAmount, prev.rate, prev.making, prev.gst);
                return { ...prev, amount: value, ...computed, baseValue: newAmount };
            });
            return;
        }

        if(name=="making"){
            const newMaking = parseFloat(value) || 0;
            setCalcForm((prev) => {
                if(prev.calcType === "weight"){
                    const computed = computeFromWeight(parseFloat(prev.weight) || 0, prev.rate, newMaking, prev.gst);
                    return { ...prev, making: value, ...computed };
                } else {
                    const amt = parseFloat(prev.amount) || 0;
                    const computed = computeFromAmount(amt, prev.rate, newMaking, prev.gst);
                    return { ...prev, making: value, ...computed, baseValue: amt };
                }
            });
            return;
        }

        if(name=="gst"){
            const newGst = parseFloat(value) || 0;
            setCalcForm((prev) => {
                if(prev.calcType === "weight"){
                    const computed = computeFromWeight(parseFloat(prev.weight) || 0, prev.rate, prev.making, newGst);
                    return { ...prev, gst: value, ...computed };
                } else {
                    const amt = parseFloat(prev.amount) || 0;
                    const computed = computeFromAmount(amt, prev.rate, prev.making, newGst);
                    return { ...prev, gst: value, ...computed, baseValue: amt };
                }
            });
            return;
        }

        setCalcForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    return(
        <div className="w-full py-2.5">
            {/*calculator header*/}
            <div className="flex justify-between mt-2">
                <span className="font-semibold text-lg"> {isGold? "Gold": "Silver"} Rate Calculator</span>
                <div className="inline-flex gap-x-3 text-md">
                    <button onClick={() => handleChange("calcType", "weight")} className={`${calcForm.calcType=="weight"?  `font-semibold ${colors.gradient}`:`${colors.lightBg} border ${colors.border}`}  rounded-lg p-1.5  hidden md:block`}>Calculate By Weight</button>
                    <button onClick={() => handleChange("calcType", "weight")} className={`${calcForm.calcType=="weight"?  `font-semibold ${colors.gradient}`:`${colors.lightBg} border ${colors.border}`}   rounded-lg p-1.5  md:hidden`}>By Weight</button>
                    <button onClick={() => handleChange("calcType", "amount")} className={`${calcForm.calcType=="amount"?  `font-semibold ${colors.gradient}`:`${colors.lightBg} border ${colors.border}`}   rounded-lg p-1.5 hidden md:block`}>Calculate By Amount</button>
                    <button onClick={() => handleChange("calcType", "amount")} className={`${calcForm.calcType=="amount"?  `font-semibold ${colors.gradient}`:`${colors.lightBg} border ${colors.border}`}   rounded-lg p-1.5 md:hidden`}>By Amount</button>
                </div>
            </div>

            {/*calculator inputs*/ }
            <div className="mt-4 flex flex-col md:flex-row gap-1.5">
                {/* purity only for gold*/}
                {isGold && <div className="flex flex-col gap-y-1 w-full md:w-[25%] ">
                    <span className="text-sm">Purity</span>
                    <div className={`flex px-1.5 py-1 gap-x-2 rounded-lg ${colors.lightBg} border ${colors.border}`}>
                        <button onClick={() => handleChange("purity", "24K")} className={`${calcForm.purity=="24K"? `${colors.gradient}`: ""} rounded-lg flex-1 p-1.5 text-sm font-semibold`}>24K</button>
                        <button onClick={() => handleChange("purity", "22K")} className={`${calcForm.purity=="22K"? `${colors.gradient}`: ""} rounded-lg flex-1 p-1.5 text-sm font-semibold`}>22K</button>
                        <button onClick={() => handleChange("purity", "18K")} className={`${calcForm.purity=="18K"? `${colors.gradient}`: ""} rounded-lg flex-1 p-1.5 text-sm font-semibold`}>18K</button>
                        <button onClick={() => handleChange("purity", "14K")} className={`${calcForm.purity=="14K"? `${colors.gradient}`: ""} rounded-lg flex-1 p-1.5 text-sm font-semibold`}>14K</button>
                    </div>
                </div>}

                <div className={`flex w-full ${isGold?"md:w-[75%]":""} gap-x-2`}>
                    {calcForm.calcType=="weight" ? <div className="flex-col gap-y-1 w-1/3">
                        <span className="text-sm">Weight (gm)</span>
                        <div className={`${colors.lightBg} border ${colors.border} px-1.5 py-1 rounded-lg flex items-center gap-x-1.5 text-sm`}>
                            <input type="text" value={calcForm.weight} onChange={(e) => handleChange("weight", e.target.value)} className="p-1.5 w-full bg-transparent outline-none focus:outline-none text-sm" placeholder="Enter weight"/>
                            <span>gm</span>
                        </div>
                    </div>:<div className="flex-col gap-y-1 w-1/3">
                        <span className="text-sm">Amount (₹)</span>
                        <div className={`${colors.lightBg} border ${colors.border} px-1.5 py-1 rounded-lg flex items-center gap-x-1.5 text-sm`}>
                            <input type="text" value={calcForm.amount} onChange={(e) => handleChange("amount", e.target.value)} className="p-1.5 w-full bg-transparent outline-none focus:outline-none text-sm" placeholder="Enter amount"/>
                            <span>₹</span>
                        </div>
                    </div>}
                    <div className="flex-col gap-y-1 w-1/3">
                        <span className="text-sm">Making (%)</span>
                        <div className={`${colors.lightBg} border ${colors.border} px-1.5 py-1 rounded-lg flex items-center gap-x-1.5 text-sm`}>
                            <input type="text" value={calcForm.making} onChange={(e) => handleChange("making", e.target.value)} className="p-1.5 w-full bg-transparent outline-none focus:outline-none text-sm" placeholder="Making %"/>
                            <span>%</span>
                        </div>
                    </div>
                    <div className="flex-col gap-y-1 w-1/3 relative" ref={gstRef}>
                        <span className="text-sm">GST</span>
                        <div
                            onClick={() => setGstOpen(prev => !prev)}
                            className={`${colors.lightBg} border ${colors.border} px-1.5 py-1 rounded-lg flex items-center justify-between gap-x-1.5 text-sm cursor-pointer select-none`}
                        >
                            <span className="p-1.5">{calcForm.gst}%</span>
                            <span className={`transition-transform duration-200 text-xs ${gstOpen ? "rotate-180" : ""}`}>▾</span>
                        </div>
                        <div className={`bg-white absolute z-10 w-full mt-1 rounded-lg border ${colors.border} overflow-hidden shadow-md transition-all duration-200 origin-top ${gstOpen ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0 pointer-events-none"}`}>
                            {["0", "3", "5"].map(val => (
                                <button
                                    key={val}
                                    onClick={() => { handleChange("gst", val); setGstOpen(false); }}
                                    className={`${String(calcForm.gst) === val ? colors.gradient : ""} w-full text-left px-3 py-2 text-sm font-semibold`}
                                >
                                    {val}%
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {errorMessage && (
                <div className="mt-2 flex items-center justify-center gap-x-1.5 text-sm font-semibold text-red-600 bg-red-50 rounded-md px-2.5 py-1.5">
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* calculator results*/}
            <div className="flex flex-col md:flex-row w-full mt-4">
                <div className={`${colors.lightBg} flex flex-col gap-y-2 md:w-1/2 px-3 py-4 text-sm border ${colors.border} rounded-tl-lg rounded-bl-lg`}>
                    <div className="inline-flex justify-between">
                        <span>Base Value</span>
                        <span className="font-semibold">{formatINR(calcForm.baseValue)}</span>
                    </div>
                    <div className="inline-flex justify-between">
                        <span>Making Charges</span>
                        <span className="font-semibold">{formatINR(calcForm.makingCharge)}</span>
                    </div>
                    <div className="inline-flex justify-between">
                        <span>GST Charges</span>
                        <span className="font-semibold">{formatINR(calcForm.gstCharge)}</span>
                    </div>
                </div>
                 {calcForm.calcType=="weight"?
                   <div className={`${colors.gradient} flex flex-col justify-center items-center md:w-1/2 px-3 py-4 text-sm border rounded-tr-lg rounded-br-lg `}>
                        <span className="text-sm">Total Amount</span>
                        <span className="text-lg font-semibold">{formatINR(calcForm.amount)}</span>
                        <span className="text-sm">Incl. All Charges</span>
                    </div>  
                    :
                    <div className={`${colors.gradient} flex flex-col justify-center items-center md:w-1/2 px-3 py-4 text-sm border rounded-tr-lg rounded-br-lg `}>
                        <span className="text-sm">{isGold?"Gold you can buy":"Silver you can buy"}</span>
                        <span className="text-lg font-semibold">{(parseFloat(calcForm.weight) || 0).toFixed(2) + "gm"}</span>
                        {/* <span className="text-sm">{"Gold Weight"}</span> */}
                    </div>
                 }

            </div>

        </div>
    )
}